import {
    Alert, AlertTitle,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem, Popover,
    Select,
    TablePagination,
    TableSortLabel,
    TextField, Typography
} from "@mui/material";
import React from "react";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {useNavigate} from "react-router-dom";
import {TABLE_ROW_LENGTH_OPTIONS} from "../../../config/table";
import {
    useDeleteVoohMutation,
    useGetVoohListsFiltersMetaQuery,
    useGetVoohPlaylistsGapsQuery,
    useGetVoohPlaylistsQuery
} from "../api/voohApiSlice";
import {FilterItemChip} from "../../drafts/types";
import {useDispatch, useSelector} from "react-redux";
import {selectCurrentFiltersState, setFilters} from "../store/voohSlice";
import {Clear, HdrStrong} from "@mui/icons-material";

import useDebounce from "../../../hooks/useDebounce";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import {VoohGap, VoohGapTemplate, VoohPlayListsFilters} from "../types";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {enqueueSnackbar} from "notistack";
import ConfirmDialog from "../../shared/confirmDialog/ConfirmDialog";
import {useTranslation} from "react-i18next";
import {visuallyHidden} from "@mui/utils";
import {Order} from "../../../config/general";
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {styled} from "@mui/material/styles";
import MuiAccordion, {AccordionProps} from '@mui/material/Accordion';
import MuiAccordionSummary, {
    AccordionSummaryProps,
    accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

import './VoohList.scss'

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({theme}) => ({
    transition: '0.3s',
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
    fontSize: '10px',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{fontSize: '0.9rem'}}/>}
        {...props}
    />
))(({theme}) => ({
    backgroundColor: 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
        {
            transform: 'rotate(90deg)',
        },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
    ...theme.applyStyles('dark', {
        backgroundColor: 'rgba(255, 255, 255, .05)',
    }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({theme}) => ({
    padding: theme.spacing(2),
    fontSize: '10px',
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));


const VoohList = () => {

    const {t, i18n} = useTranslation(); // not passing any namespace will use the defaultNS (by default set to 'translation')
    const navigate = useNavigate();

    const [searchByName, setSearchByName] = React.useState<string>('');
    const currentFiltersState = useSelector(selectCurrentFiltersState);

    const [rowLength, setRowLength] = React.useState(TABLE_ROW_LENGTH_OPTIONS[0]);
    const [expandedVoohGap, setExpandedVoohGap] = React.useState<string | false>(false);


    const page = currentFiltersState.page;
    let params = [`items=${rowLength.toString()}`];
    let filterItemChips: FilterItemChip[] = []
    for (const queryKey in currentFiltersState) {
        const value = currentFiltersState[queryKey as keyof typeof currentFiltersState]
        if (Array.isArray(value)) {
            value.forEach(item => {
                params.push(`q[${queryKey}][]=${item.toString()}`)
                filterItemChips.push({name: queryKey, value: item, isArray: true})
            })
        } else if (queryKey === 'page') {
            params.push(`page=${value.toString()}`)
        } else if (value) {
            params.push(`q[${queryKey}]=${value.toString()}`)
            if (queryKey !== 's') {
                filterItemChips.push({name: queryKey, value: value.toString(), isArray: false})
            }
        }
    }

    const {isLoading, isError, isSuccess, data} =
        useGetVoohPlaylistsQuery(params.join('&').toString());


    const {
        isLoading: loadingVoohPlaylistsGaps,
        isError: isErrorVoohPlaylistsGaps,
        isSuccess: isSuccessVoohPlaylistsGaps,
        data: voohPlaylistsGaps
    } =
        useGetVoohPlaylistsGapsQuery();

    const [deleteVooh, {isLoading: deletingVooh}] = useDeleteVoohMutation()

    const [skipFetchingVooh, setSkipFetchingVooh] = React.useState(false);
    const [openRemoveVoohDialog, setOpenRemoveVoohDialog] = React.useState<string | null>(null)
    const [orderBy, setOrderBy] = React.useState<string>('name');
    const [order, setOrder] = React.useState<Order>('asc');

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;


    const {
        isLoading: loadingFiltersMeta,
        isError: isFiltersMetaError,
        isSuccess: isSuccessFiltersMeta,
        data: filtersMetaData
    } =
        useGetVoohListsFiltersMetaQuery();

    const dispatch = useDispatch();


    const voohLists = data?.data

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        dispatch(setFilters({
            ...currentFiltersState,
            s: `${property} ${isAsc ? 'desc' : 'asc'}`,
            page: 1
        }))
    };

    const createSortHandler =
        (property: string) => (event: React.MouseEvent<unknown>) => {
            handleRequestSort(event, property);
        };

    const handleChangeExpansionVoohGap =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpandedVoohGap(isExpanded ? panel : false);
        };



    useDebounce(() => {
            dispatch(setFilters({
                ...currentFiltersState,
                nameCont: searchByName,
                page: 1
            }))
        }, [searchByName], 800
    );

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setRowLength(parseInt(event.target.value, 10))
    }

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };


    function removeFilter(filterElement: FilterItemChip) {
        if (filterElement.isArray) {
            dispatch(setFilters({
                ...currentFiltersState,
                page: 1,
                [filterElement.name]: (currentFiltersState[filterElement.name as keyof VoohPlayListsFilters] as string[]).filter(el => el !== filterElement.value)
            }))
        } else {
            if (filterElement.name === 'nameCont') {
                setSearchByName('');
            }
            dispatch(setFilters({
                ...currentFiltersState,
                page: 1,
                [filterElement.name]: ''
            }))
        }
    }

    const deleteVoohMutation = async (voohId: string) => {
        try {
            const isLastItem = voohLists?.length === 1;
            if (isLastItem && page > 0) {
                setSkipFetchingVooh(true);
            }
            const result = await deleteVooh(voohId).unwrap()
            if (!result?.error) {
                if (isLastItem && page > 0) {
                    setSkipFetchingVooh(false);
                    dispatch(setFilters({
                        ...currentFiltersState,
                        page: page - 1
                    }))
                }
                enqueueSnackbar('Pomyślnie usunięto ramówke', {variant: 'success'});
                setOpenRemoveVoohDialog(null)
            }
        } catch (err: any) {
            console.log(err)
            enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
        }
    }


    return (<Box className="VoohList">
        <Box className={'page-header'}>
            <h1>Utworzone ramówki</h1>
        </Box>

        {(isLoading || loadingFiltersMeta) && (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                <CircularProgress size={100}/>
            </Box>)}
        {(voohLists && filtersMetaData) && (<>
            <Grid container spacing={2}>
                <Grid item xs={4} alignItems="baseline">
                    <TextField
                        size="small"
                        value={searchByName}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchByName(event.target.value);
                        }}
                        InputProps={{
                            endAdornment: <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => {
                                    setSearchByName('')
                                    dispatch(setFilters({
                                        ...currentFiltersState,
                                        nameCont: searchByName,
                                        page: 1
                                    }))
                                }
                                }
                                edge="end"
                            >
                                {<Clear/>}
                            </IconButton>,
                        }}
                        margin="normal"
                        variant={"outlined"}
                        required
                        fullWidth
                        id="voohPlayListName"
                        label="Nazwa ramówki"
                        name="voohPlayListName"
                        autoFocus></TextField>
                </Grid>
                <Grid item xs={2}>
                    <FormControl fullWidth sx={{marginTop: '15px'}}>
                        <InputLabel shrink id="demo-simple-select-label">Status</InputLabel>
                        <Select
                            size="small"
                            variant={"outlined"}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Status"
                            value={currentFiltersState.voohPlaylistStatusEq || ''}
                            notched
                            onChange={(event) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    voohPlaylistStatusEq: event.target.value,
                                    page: 1
                                }))
                            }}
                        >
                            {filtersMetaData.data.filtering.fields.voohPlaylistStatusEq.allowedValues.map((key) => (
                                <MenuItem
                                    key={key}
                                    value={key}>{t(`vooh.status.${key}`)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <DatePicker
                        maxDate={dayjs(new Date(currentFiltersState.endAtGteq))}
                        onChange={(event) => {
                            dispatch(setFilters({
                                ...currentFiltersState,
                                startAtLteq: dayjs(event).date() ? dayjs(event).format('YYYY-MM-DD') : '',
                                page: 1
                            }))
                        }}
                        value={currentFiltersState.startAtLteq ? dayjs(currentFiltersState.startAtLteq) : null}
                        sx={{marginTop: '14px', width: '100%'}} slotProps={{textField: {size: 'small'}}} label="Od"/>
                </Grid>
                <Grid item xs={2}>
                    <DatePicker
                        minDate={currentFiltersState.startAtLteq ? dayjs(currentFiltersState.startAtLteq) : undefined}
                        onChange={(event) => {
                            dispatch(setFilters({
                                ...currentFiltersState,
                                endAtGteq: dayjs(event).date() ? dayjs(event).format('YYYY-MM-DD') : '',
                                page: 1
                            }))
                        }}
                        sx={{marginTop: '14px', width: '100%'}}
                        value={currentFiltersState.endAtGteq ? dayjs(currentFiltersState.endAtGteq) : null}
                        slotProps={{textField: {size: 'small'}}} label="Do"/>
                </Grid>
                <Grid item xs={10}>
                    <FormControl fullWidth>
                        <InputLabel id="demo-multiple-chip-label">Tagi</InputLabel>
                        <Select
                            size="small"
                            labelId="demo-multiple-chip-label"
                            id="demo-multiple-chip"
                            multiple
                            value={currentFiltersState.tagsNameIn}
                            onChange={(event) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    tagsNameIn: typeof event.target.value === "string" ? [event.target.value] : event.target.value,
                                    page: 1
                                }))
                            }}
                            input={<OutlinedInput id="select-multiple-chip" label="Chip"/>}

                            MenuProps={MenuProps}
                        >
                            {filtersMetaData.data.filtering.fields.tagsNameIn.allowedValues.map((tagsNameIn) => (
                                <MenuItem
                                    key={tagsNameIn}
                                    value={tagsNameIn}
                                >
                                    {tagsNameIn}
                                </MenuItem>
                            ))}
                            {
                                filtersMetaData.data.filtering.fields.tagsNameIn.allowedValues?.length === 0 && (
                                    <MenuItem
                                        disabled={true}
                                        value={''}
                                    >
                                        <em>Brak utworzonych tagów</em>
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={8}>
                </Grid>
            </Grid>
            <Grid container spacing={2} pb={2}>
                {filterItemChips.map((filter) => {

                    let chipValue = filter.value

                    if (filter.name === 'voohPlaylistStatusEq') {
                        chipValue = t(`vooh.status.${filter.value}`)
                    }


                    return (
                        <Grid item key={filter.value}>
                            <Chip
                                color="info"
                                label={(
                                    <React.Fragment>{t(`filters.keys.${filter.name}`) + ': '}<strong>{chipValue}</strong></React.Fragment>)}
                                onClick={() => {
                                }}
                                onDelete={() => removeFilter(filter)}
                            />
                        </Grid>)
                })}
            </Grid>

            {(voohPlaylistsGaps && voohPlaylistsGaps?.length > 0) && (<Alert severity="warning">
                <AlertTitle><strong>Nieużywane nośniki</strong></AlertTitle>
                <Box sx={{display: 'flex', gap: '20px', alignItems: 'baseline'}}><Typography> Nie wszystkie nośniki są
                    użyte w
                    ramówkach</Typography>
                    <Button aria-describedby={id} variant="outlined" sx={{marignLeft: '20px'}} onClick={handleClick}>
                        Pokaż nieużywane nośniki
                    </Button></Box>
                <Popover
                    sx={{maxWidth: '70%'}}
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{maxHeight: '600px', overflow: 'auto', marginTop: '10px'}}>
                        {
                            voohPlaylistsGaps && voohPlaylistsGaps.map((gap: VoohGapTemplate) => {
                                return (
                                    <Accordion expanded={expandedVoohGap === gap.title}
                                               onChange={handleChangeExpansionVoohGap(gap.title)}>
                                        <AccordionSummary>
                                            <Typography component="span" sx={{fontSize: '14px'}}>{gap.title}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography sx={{fontSize: '14px'}}>
                                                {gap.media.join(' ,')}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>)
                            })
                        }
                    </Box>
                </Popover>
            </Alert>)}
            <TableContainer component={Paper}>
                <Table sx={{minWidth: 650}} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={createSortHandler('name')}
                                >
                                    Nazwa ramówki
                                    {orderBy === 'name' ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Status</TableCell>
                            <TableCell align="right">Tagi</TableCell>
                            <TableCell align="right">Domyślna ramówka</TableCell>
                            <TableCell align="right">Data emisji</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {voohLists.map((row, index) => (
                            <TableRow
                                className={row.defaultPlaylist ? 'VoohList__row--defaultPlaylist' : undefined}
                                key={row.id}
                                sx={{'&:last-child td, &:last-child th': {border: 0}}}
                            >
                                <TableCell component="th" scope="row">
                                    {row.name}
                                </TableCell>
                                <TableCell align="right">{t(`vooh.status.${row.voohPlaylistStatus}`)}</TableCell>
                                <TableCell align="right">{row.tagList.join(', ')}</TableCell>
                                <TableCell align="right">{row.defaultPlaylist && (<CheckIcon></CheckIcon>)}</TableCell>
                                <TableCell align="right">
                                    {!row.defaultPlaylist && row.startAt && row.endAt && (
                                        <>
                                            <p>
                                                od {new Date(row.startAt).toLocaleDateString()}
                                            </p>
                                            <p>
                                                do {new Date(row.endAt).toLocaleDateString()}
                                            </p>
                                        </>
                                    )}
                                </TableCell>
                                <TableCell align="right" width={'5%'}>
                                    <ButtonGroup variant="text" aria-label="Basic button group">
                                        <Button  sx={{paddingX: 3}} onClick={() => navigate(`/emissions/new-schedule?copyFrom=${row.id}`)}>
                                            Kopiuj
                                        </Button>
                                        <Button onClick={() => navigate(`/emissions/vooh-list/${row.id}`)}>
                                            Edycja Ramówki
                                        </Button>
                                        <Button color="error" onClick={() => {
                                            setOpenRemoveVoohDialog(row.id.toString())
                                        }}>Usuń ramówkę</Button>
                                    </ButtonGroup>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={TABLE_ROW_LENGTH_OPTIONS}
                component="div"
                count={data.meta.count}
                rowsPerPage={rowLength}
                page={data.meta.page - 1}
                onPageChange={(e, page) => dispatch(setFilters({
                    ...currentFiltersState,
                    page: page + 1
                }))}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {openRemoveVoohDialog && (<ConfirmDialog title={'Usuwanie vooh'}
                                                     description={`Czy na pewno chcesz usunąć ramówke?`}
                                                     confirmText={'Usuń'}
                                                     open={!!openRemoveVoohDialog}
                                                     onConfirm={() => {
                                                         deleteVoohMutation(openRemoveVoohDialog)
                                                     }}
                                                     onCancel={() => {
                                                         setOpenRemoveVoohDialog(null)

                                                     }}></ConfirmDialog>)}

        </>)}
    </Box>)
}

export default VoohList
